"use strict";
/**
 * Polyfill for async iteration of `ReadableStream`
 * (`ReadableStream.prototype[Symbol.asyncIterator]`).
 *
 * Safari only shipped this API in 26.4; every earlier version is missing it. pdf.js (v5+) reads
 * its text and operator-list streams with `for await (const chunk of stream)`, most notably inside
 * `PDFPageProxy.getTextContent()`, which throws "undefined is not a function" in those Safari
 * versions because the async-iterator method is absent.
 *
 * Importing this module for its side effect installs the iterator when it is missing. It is a
 * no-op in Chromium and Firefox, which have supported the API since Chrome 124 and Firefox 110.
 *
 * Temporary shim — pdf.js tracks the upstream fix (a regression from PR #20648) in
 * https://github.com/mozilla/pdf.js/issues/20973. Remove this once we upgrade to a pdf.js release
 * that no longer relies on ReadableStream async iteration.
 *
 * @see https://caniuse.com/mdn-api_readablestream_--asynciterator
 */
var __await = (this && this.__await) || function (v) { return this instanceof __await ? (this.v = v, this) : new __await(v); }
var __asyncGenerator = (this && this.__asyncGenerator) || function (thisArg, _arguments, generator) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var g = generator.apply(thisArg, _arguments || []), i, q = [];
    return i = {}, verb("next"), verb("throw"), verb("return", awaitReturn), i[Symbol.asyncIterator] = function () { return this; }, i;
    function awaitReturn(f) { return function (v) { return Promise.resolve(v).then(f, reject); }; }
    function verb(n, f) { if (g[n]) { i[n] = function (v) { return new Promise(function (a, b) { q.push([n, v, a, b]) > 1 || resume(n, v); }); }; if (f) i[n] = f(i[n]); } }
    function resume(n, v) { try { step(g[n](v)); } catch (e) { settle(q[0][3], e); } }
    function step(r) { r.value instanceof __await ? Promise.resolve(r.value.v).then(fulfill, reject) : settle(q[0][2], r); }
    function fulfill(value) { resume("next", value); }
    function reject(value) { resume("throw", value); }
    function settle(f, v) { if (f(v), q.shift(), q.length) resume(q[0][0], q[0][1]); }
};
function readableStreamAsyncIterator() {
    return __asyncGenerator(this, arguments, function* readableStreamAsyncIterator_1() {
        const reader = this.getReader();
        try {
            let result = yield __await(reader.read());
            while (!result.done) {
                yield yield __await(result.value);
                // eslint-disable-next-line no-await-in-loop -- reading a stream is inherently sequential.
                result = yield __await(reader.read());
            }
        }
        finally {
            reader.releaseLock();
        }
    });
}
if (typeof ReadableStream !== 'undefined' &&
    typeof ReadableStream.prototype[Symbol.asyncIterator] !== 'function') {
    // eslint-disable-next-line no-extend-native
    ReadableStream.prototype[Symbol.asyncIterator] = readableStreamAsyncIterator;
}
